import React, { useState, useEffect } from 'react';
import {
  Modal,
  Upload,
  Button,
  message,
  Typography,
  Alert,
  Space,
  Table,
  Tag,
  Steps,
  Result,
  Spin
} from 'antd';
import {
  UploadOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import * as XLSX from 'xlsx';
import { useTranslation } from "react-i18next";

const { Text, Paragraph } = Typography;

interface InvoiceBulkUploadModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customers: Array<{ id: string; name: string }>;
  accounts: Array<{ id: string; code: string; name: string }>;
  companyId: string;
}

interface ParsedInvoiceRow {
  rowNum: number;
  invoiceNo: string;
  customerName: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  paymentTerms: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discount: number;
  accountCode: string;
  lineTotal: number;
  // resolved
  customerId?: string;
  accountId?: string;
  errors: string[];
  warnings: string[];
}

interface GroupedInvoice {
  invoiceNo: string;
  customerName: string;
  customerId: string;
  invoiceDate: string;
  dueDate: string;
  currency: string;
  paymentTerms: string;
  totalAmount: number;
  itemCount: number;
  items: Array<{
    description: string;
    quantity: number;
    unit: string;
    unitPrice: number;
    discount: number;
    lineTotal: number;
    accountId?: string;
    accountCode?: string;
  }>;
  errors: string[];
}

interface BulkResult {
  created: number;
  failed: number;
  errors: Array<{ invoiceNo: string; error: string }>;
}

const TEMPLATE_HEADERS = [
  'Invoice No', 'Customer', 'Invoice Date', 'Due Date', 'Currency',
  'Payment Terms', 'Description', 'Qty', 'Unit', 'Unit Price',
  'Discount', 'Account Code', 'Line Total'
];

const InvoiceBulkUploadModal: React.FC<InvoiceBulkUploadModalProps> = ({
  visible,
  onClose,
  onSuccess,
  customers,
  accounts,
  companyId
}) => {
    const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [parsedRows, setParsedRows] = useState<ParsedInvoiceRow[]>([]);
  const [groupedInvoices, setGroupedInvoices] = useState<GroupedInvoice[]>([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  useEffect(() => {
    if (!visible) {
      setStep(0);
      setFileList([]);
      setParsedRows([]);
      setGroupedInvoices([]);
      setResult(null);
    }
  }, [visible]);

  const downloadTemplate = () => {
    const sampleRows = [
      ['INV/SL/2026/03/0001', 'ABC Corp', '2026-03-01', '2026-03-31', 'HKD', '30 Days', 'Warehouse Storage', '10', 'Rack(s)', '750', '0', '460000', '7500'],
      ['INV/SL/2026/03/0001', 'ABC Corp', '2026-03-01', '2026-03-31', 'HKD', '30 Days', 'Cargo Handling', '5', 'Order(s)', '110', '0', '461000', '550'],
      ['INV/SL/2026/03/0002', 'XYZ Ltd', '2026-03-05', '2026-04-04', 'USD', '30 Days', 'International Freight', '1', 'unit(s)', '2000', '0', '430004', '2000'],
    ];

    const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...sampleRows]);
    ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 18 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Invoice Import');
    XLSX.writeFile(wb, 'invoice_import_template.xlsx');
  };

  const parseExcelDate = (val: any): string => {
    if (!val) return '';
    if (typeof val === 'number') {
      // Excel serial date
      const date = new Date((val - 25569) * 86400 * 1000);
      return date.toISOString().split('T')[0];
    }
    if (typeof val === 'string') {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
      return val;
    }
    if (val instanceof Date) return val.toISOString().split('T')[0];
    return String(val);
  };

  const processFile = async (file: File) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rawRows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

    if (rawRows.length === 0) {
      message.error(t('File is empty'));
      return;
    }

    // Build lookup maps
    const customerMap = new Map<string, string>();
    customers.forEach(c => customerMap.set(c.name.toLowerCase().trim(), c.id));

    const accountMap = new Map<string, string>();
    accounts.forEach(a => accountMap.set(a.code.trim(), a.id));

    const rows: ParsedInvoiceRow[] = rawRows.map((row, idx) => {
      const errors: string[] = [];
      const warnings: string[] = [];

      const invoiceNo = String(row['Invoice No'] || '').trim();
      const customerName = String(row['Customer'] || '').trim();
      const invoiceDate = parseExcelDate(row['Invoice Date']);
      const dueDate = parseExcelDate(row['Due Date']);
      const currency = String(row['Currency'] || 'HKD').trim().toUpperCase();
      const paymentTerms = String(row['Payment Terms'] || '').trim();
      const description = String(row['Description'] || '').trim();
      const quantity = Number(row['Qty'] || 0);
      const unit = String(row['Unit'] || 'unit(s)').trim();
      const unitPrice = Number(row['Unit Price'] || 0);
      const discount = Number(row['Discount'] || 0);
      const accountCode = String(row['Account Code'] || '').trim();
      let lineTotal = Number(row['Line Total'] || 0);

      // Auto-calc line total if not provided
      if (!lineTotal && quantity && unitPrice) {
        lineTotal = Math.round((quantity * unitPrice - discount) * 100) / 100;
      }

      // Validate
      // Copy, not data: these are only rendered in the preview table and counted.
      // The column names inside them stay raw because they name literal headers.
      if (!invoiceNo) errors.push(t('Invoice No is required'));
      if (!customerName) errors.push(t('Customer is required'));
      if (!description) errors.push(t('Description is required'));
      if (quantity <= 0) errors.push(t('Qty must be > 0'));
      if (unitPrice <= 0) errors.push(t('Unit Price must be > 0'));

      // Resolve customer
      const customerId = customerMap.get(customerName.toLowerCase().trim());
      if (customerName && !customerId) errors.push(t('Customer "{{customerName}}" not found', { customerName }));

      // Resolve account
      let accountId: string | undefined;
      if (accountCode) {
        accountId = accountMap.get(accountCode);
        if (!accountId) warnings.push(t('Account code "{{accountCode}}" not found, will use default', { accountCode }));
      }

      return {
        rowNum: idx + 2,
        invoiceNo, customerName, invoiceDate, dueDate, currency,
        paymentTerms, description, quantity, unit, unitPrice,
        discount, accountCode, lineTotal,
        customerId, accountId, errors, warnings
      };
    });

    setParsedRows(rows);

    // Group by invoice number
    const groups = new Map<string, GroupedInvoice>();
    for (const row of rows) {
      if (!row.invoiceNo) continue;
      if (!groups.has(row.invoiceNo)) {
        groups.set(row.invoiceNo, {
          invoiceNo: row.invoiceNo,
          customerName: row.customerName,
          customerId: row.customerId || '',
          invoiceDate: row.invoiceDate,
          dueDate: row.dueDate,
          currency: row.currency,
          paymentTerms: row.paymentTerms,
          totalAmount: 0,
          itemCount: 0,
          items: [],
          errors: []
        });
      }
      const group = groups.get(row.invoiceNo)!;
      group.items.push({
        description: row.description,
        quantity: row.quantity,
        unit: row.unit,
        unitPrice: row.unitPrice,
        discount: row.discount,
        lineTotal: row.lineTotal,
        accountId: row.accountId,
        accountCode: row.accountCode
      });
      group.totalAmount += row.lineTotal;
      group.itemCount++;
      group.errors.push(...row.errors);
    }

    setGroupedInvoices(Array.from(groups.values()));
    setStep(1);
  };

  const handleUpload = async () => {
    const validInvoices = groupedInvoices.filter(g => g.errors.length === 0 && g.customerId);
    if (validInvoices.length === 0) {
      message.error(t('No valid invoices to import'));
      return;
    }

    setUploading(true);
    const results: BulkResult = { created: 0, failed: 0, errors: [] };

    const token = localStorage.getItem('authToken');
    const baseURL = (import.meta as any).env?.VITE_API_URL || (import.meta as any).env?.VITE_API_BASE_URL || '/api';

    for (const inv of validInvoices) {
      try {
        const body = {
          invoiceNo: inv.invoiceNo,
          customerId: inv.customerId,
          invoiceDate: inv.invoiceDate || undefined,
          dueDate: inv.dueDate || undefined,
          currency: inv.currency || 'HKD',
          paymentTerms: inv.paymentTerms || undefined,
          totalAmount: Math.round(inv.totalAmount * 100) / 100,
          status: 'DRAFT',
          items: inv.items.map((item, i) => ({
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            unitPrice: item.unitPrice,
            discount: item.discount || 0,
            lineTotal: item.lineTotal,
            accountId: item.accountId || undefined,
            sortOrder: i
          }))
        };

        const resp = await fetch(`${baseURL}/v1/accounting/invoices`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(body)
        });

        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.error || errData.message || `HTTP ${resp.status}`);
        }
        results.created++;
      } catch (e: any) {
        results.failed++;
        results.errors.push({ invoiceNo: inv.invoiceNo, error: e.message });
      }
    }

    setResult(results);
    setUploading(false);
    setStep(2);

    if (results.created > 0) {
      message.success(t('Created {{created}} invoice(s)', { created: results.created }));
      onSuccess();
    }
  };

  const totalErrors = parsedRows.filter(r => r.errors.length > 0).length;
  const totalValid = groupedInvoices.filter(g => g.errors.length === 0 && g.customerId).length;

  const previewColumns = [
    { title: t('Invoice No'), dataIndex: 'invoiceNo', key: 'invoiceNo', width: 160 },
    { title: t('Customer'), dataIndex: 'customerName', key: 'customer', width: 180 },
    { title: t('Items'), dataIndex: 'itemCount', key: 'items', width: 60, render: (v: number) => v },
    { title: t('Currency'), dataIndex: 'currency', key: 'currency', width: 70 },
    {
      title: t('Total'),
      dataIndex: 'totalAmount',
      key: 'total',
      width: 110,
      render: (v: number, r: GroupedInvoice) => `${r.currency} ${v.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    },
    {
      title: t('Status'),
      key: 'status',
      width: 120,
      render: (_: any, r: GroupedInvoice) =>
        r.errors.length > 0
          ? <Tag color="red">{r.errors.length} error(s)</Tag>
          : <Tag color="green">{t('Valid')}</Tag>
    }
  ];

  return (
    <Modal
      title={<Space><FileExcelOutlined style={{ color: '#52c41a' }} /><span>{t('Bulk Import Invoices')}</span></Space>}
      open={visible}
      onCancel={onClose}
      width={800}
      footer={
        step === 0 ? [
          <Button key="cancel" onClick={onClose}>{t('Cancel')}</Button>
        ] : step === 1 ? [
          <Button key="back" onClick={() => { setStep(0); setParsedRows([]); setGroupedInvoices([]); setFileList([]); }}>{t('Back')}</Button>,
          <Button key="import" type="primary" loading={uploading} onClick={handleUpload} disabled={totalValid === 0}>
            {t('Import')} {totalValid} {t('Invoice(s)')}
                        </Button>
        ] : [
          <Button key="close" type="primary" onClick={onClose}>{t('Close')}</Button>
        ]
      }
    >
      <Steps current={step} size="small" style={{ marginBottom: 24 }} items={[
        { title: t('Upload File') },
        { title: t('Preview & Confirm') },
        { title: t('Result') }
      ]} />

      {step === 0 && (
        <div>
          <Alert
            type="info"
            showIcon
            message={t('Import Invoice from Excel')}
            description={
              <div>
                <Paragraph style={{ marginBottom: 8 }}>
                  {t('Upload an Excel file (.xlsx) with these columns. Multiple rows with the same Invoice No will be grouped as line items of one invoice.')}
                                        </Paragraph>
                {/* The column names below are the literal spreadsheet headers the parser
                    matches (TEMPLATE_HEADERS / row['…']). They must stay raw English in
                    every locale — translating them would tell the user to name a column
                    something the importer never looks for. Only the prose is translated. */}
                <ul style={{ marginBottom: 0, paddingLeft: 20, fontSize: 12 }}>
                  <li><Text code>Invoice No</Text> — {t('Invoice number (rows with same number = same invoice)')}</li>
                  <li><Text code>Customer</Text> — {t('Customer name (must match existing customer)')}</li>
                  <li><Text code>Invoice Date</Text> — {t('Date (YYYY-MM-DD)')}</li>
                  <li><Text code>Due Date</Text> — {t('Due date (optional)')}</li>
                  <li><Text code>Currency</Text> — {t('HKD, USD, etc. (default: HKD)')}</li>
                  <li><Text code>Payment Terms</Text> — {t('e.g. "30 Days" (optional)')}</li>
                  <li><Text code>Description</Text> — {t('Line item description')}</li>
                  <li><Text code>Qty</Text> — {t('Quantity')}</li>
                  <li><Text code>Unit</Text> — {t('Unit of measure (default: unit(s))')}</li>
                  <li><Text code>Unit Price</Text> — {t('Price per unit')}</li>
                  <li><Text code>Discount</Text> — {t('Discount amount (optional)')}</li>
                  <li><Text code>Account Code</Text> — {t('Account code (optional)')}</li>
                  <li><Text code>Line Total</Text> — {t('Line total (auto-calculated if blank)')}</li>
                </ul>
              </div>
            }
            style={{ marginBottom: 16 }}
          />

          <Space direction="vertical" style={{ width: '100%' }}>
            <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
              {t('Download Template')}
                                      </Button>

            <Upload.Dragger
              fileList={fileList}
              beforeUpload={(file) => {
                setFileList([file as any]);
                processFile(file);
                return false;
              }}
              onRemove={() => {
                setFileList([]);
                setParsedRows([]);
                setGroupedInvoices([]);
              }}
              accept=".xlsx,.xls,.csv"
              maxCount={1}
            >
              <p className="ant-upload-drag-icon"><FileExcelOutlined style={{ fontSize: 40, color: '#52c41a' }} /></p>
              <p className="ant-upload-text">{t('Click or drag Excel file to upload')}</p>
              <p className="ant-upload-hint">{t('Supports .xlsx, .xls, .csv')}</p>
            </Upload.Dragger>
          </Space>
        </div>
      )}

      {step === 1 && (
        <div>
          {uploading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>{t('Importing invoices...')}</div>
            </div>
          ) : (
            <>
              <Alert
                type={totalErrors > 0 ? 'warning' : 'success'}
                showIcon
                message={`${groupedInvoices.length} invoice(s) found — ${totalValid} valid, ${groupedInvoices.length - totalValid} with errors`}
                style={{ marginBottom: 16 }}
              />

              <Table
                dataSource={groupedInvoices}
                columns={previewColumns}
                rowKey="invoiceNo"
                size="small"
                pagination={false}
                scroll={{ y: 300 }}
                expandable={{
                  expandedRowRender: (record) => (
                    <div>
                      {record.errors.length > 0 && (
                        <div style={{ marginBottom: 8 }}>
                          {[...new Set(record.errors)].map((e, i) => (
                            <Tag color="red" key={i}>{e}</Tag>
                          ))}
                        </div>
                      )}
                      <Table
                        dataSource={record.items}
                        size="small"
                        pagination={false}
                        rowKey={(_, i) => String(i)}
                        columns={[
                          { title: t('Description'), dataIndex: 'description', key: 'desc' },
                          { title: t('Qty'), dataIndex: 'quantity', key: 'qty', width: 60 },
                          { title: t('Unit'), dataIndex: 'unit', key: 'unit', width: 80 },
                          { title: t('Price'), dataIndex: 'unitPrice', key: 'price', width: 80, render: (v: number) => v.toFixed(2) },
                          { title: t('Account'), dataIndex: 'accountCode', key: 'acct', width: 90 },
                          { title: t('Total'), dataIndex: 'lineTotal', key: 'total', width: 100, render: (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2 }) }
                        ]}
                      />
                    </div>
                  )
                }}
              />
            </>
          )}
        </div>
      )}

      {step === 2 && result && (
        <Result
          status={result.failed === 0 ? 'success' : 'warning'}
          title={`Import Complete`}
          subTitle={`Created: ${result.created} | Failed: ${result.failed}`}
          extra={
            result.errors.length > 0 && (
              <Table
                dataSource={result.errors}
                size="small"
                pagination={false}
                rowKey="invoiceNo"
                columns={[
                  { title: t('Invoice No'), dataIndex: 'invoiceNo', key: 'inv', width: 160 },
                  { title: t('Error'), dataIndex: 'error', key: 'err', render: (t: string) => <Text type="danger">{t}</Text> }
                ]}
              />
            )
          }
        />
      )}
    </Modal>
  );
};

export default InvoiceBulkUploadModal;
